import { Suspense, useState, useEffect } from 'react'
import Section from './_Section'
import { createMarkup, createGradientElement } from '../../helpers/markup'
import { addBgImageClass, lightOrDark, transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { ArrowRightIcon, PlayIcon, XMarkIcon } from '@heroicons/react/24/solid'
import Image from '../helpers/Image'
import HeroTitle from '../helpers/CardListingHeroTitle'
import Modal from 'react-modal'
import Loader from '../helpers/Loader'
import Picture from '../helpers/Picture'
Modal.setAppElement('#root')

const customModalStyle = {
  content: {
    top: '50%',
    left: '50%',
    width: '75vw',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    translate: '-50% -50%',
    aspectRatio: '16/10',
    maxHeight: '95vh',
  },
}

async function getYTEmbed(url) {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=${url}&format=json`)
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const pushEngageEvent = (e, type, props) => {
  umbEngage(
    "send",
    "event",
    "Component interaction",
    `${type} card click`,
    props?.title?.toString().trim()
  )
}

const CardListBGImage = (props) => {
  const { cardItem, i } = props
  const [modalIsOpen, setIsOpen] = useState(false)
  const [videoEmbed, setVideoEmbed] = useState(null)
  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [gradientColor, setgradientColor] = useState('#fff')

  useEffect(() => {
    const bgColor = props.cardBackgroundColour.replace('#', '')
    const fgColor = props.imageGradientColour.replace('#', '')
    setBackgroundColor((prevState) => (bgColor ? `#${bgColor}` : prevState))
    setgradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  function openModal() {
    setIsOpen(true)
  }

  async function afterOpenModal() {
    // references are now sync'd and can be accessed.
    // subtitle.style.color = '#f00';
    const embedCode = getYTEmbed(cardItem.videoUrl)
    embedCode.then((res) => {
      if (!res.error) {
        setVideoEmbed(res)
      }
    })
  }

  function closeModal() {
    setIsOpen(false)
  }

  return (
    <div
      key={i}
      style={{
        '--mobile-bg-url': cardItem.mobileImage ? `url(${cardItem.mobileImage.src})` : null,
        '--desktop-bg-url': cardItem.desktopImage ? `url(${cardItem.desktopImage.src})` : null,
        '--bg-color': backgroundColor,
        '--light-or-dark': lightOrDark(backgroundColor),
      }}
      className={`relative w-full pt-4 px-8 pb-8 rounded-2xl 
        
        md:[&:nth-child(3n+3)]:col-start-5 
        md:[&:nth-child(3n+2)]:col-start-3 
        md:[&:nth-child(3n+1)]:col-start-1 
        md:[&:last-child:first-child]:col-start-3 
        md:[&:last-child:nth-child(3n+1)]:col-start-3 
        md:[&:nth-last-child(2):first-child]:col-start-2 
        md:[&:nth-last-child(2):nth-child(3n+1)]:col-start-2 
        md:[&:nth-child(2):last-child]:col-start-4 
        md:[&:last-child:nth-child(3n+2)]:col-start-4 
        
        col-span-2 bg-[color:var(--bg-color)] text-${lightOrDark(backgroundColor)} h-full overflow-hidden ${addBgImageClass(cardItem.desktopImage?.src, cardItem.mobileImage?.src)}`}
    >
      {props.imageGradientColour &&
        createGradientElement({
          type: props.imageGradientType,
          fromColor: gradientColor,
        })}
      <div
        className={`relative min-h-96 h-full mt-auto flex flex-col ${cardItem.videoUrl ? 'justify-between' : 'justify-end'}`}
      >
        {cardItem.videoUrl ? (
          <ButtonEl className="h-20 w-20 mx-auto mt-20" onClick={openModal} theme="none">
            <PlayIcon role="none" className="text-current" />
            <span className="sr-only">Play video</span>
          </ButtonEl>
        ) : null}
        <div>
          {cardItem.title && !cardItem.useSmallTitle && (
            <h2
              className={`block font-bold ${cardItem.useSmallTitle ? 'text-lg' : 'text-2xl'} ${cardItem.titleAlignment ? `text-${cardItem.titleAlignment.toLowerCase()}` : null}`}
            >
              {cardItem.title}
            </h2>
          )}
          {cardItem.title && cardItem.useSmallTitle && (
            <h3
              className={`block  font-bold ${cardItem.useSmallTitle ? 'text-lg' : 'text-2xl'} ${cardItem.titleAlignment ? `text-${cardItem.titleAlignment.toLowerCase()}` : null}`}
            >
              {cardItem.title}
            </h3>
          )}
          {cardItem.description && (
            <div className="mt-2" data-rte="true" dangerouslySetInnerHTML={createMarkup(cardItem.description)} />
          )}
          {cardItem.ctaLinkItem.length != 0 && (
            <div
              className={`flex flex-col items-${props.textAlignment.flex === 'around' ? 'start' : props.textAlignment.flex} gap-6 mt-10`}
            >
              {cardItem.ctaLinkItem.map((ctaLinkItem, i) => (
                <ButtonEl
                  key={i}
                  link={ctaLinkItem.link}
                  theme={ctaLinkItem.linkType.toLowerCase()}
                  icon={ctaLinkItem.linkIconClass}
                  onClick={(e) => { pushEngageEvent(e, 'Image', cardItem)}}
                  overrideEngagePush={true}
                />
              ))}
            </div>
          )}
          {/* <button onClick={openModal} className='bg-secondary-500 text-secondary-500-text'>Open Modal</button> */}
        </div>
      </div>
      {cardItem.videoUrl ? (
        <Modal
          isOpen={modalIsOpen}
          onAfterOpen={afterOpenModal}
          onRequestClose={closeModal}
          style={customModalStyle}
          contentLabel="Example Modal"
          aria={{
            labelledby: `modal-heading-${i}`,
          }}
        >
          <ButtonEl className="right-5 absolute" onClick={closeModal} theme="none">
            <XMarkIcon role="none" className="w-6 h-6 flex-shrink text-current" />
            <span className="sr-only">Close this alert</span>
          </ButtonEl>
          <div data-type="video-modal-content" className="m-0 relative w-full">
            {videoEmbed && (
              <div id={`modal-heading-${i}`} className="absolute -top-[1000px] opacity-0">
                {videoEmbed.title}
              </div>
            )}
            <Suspense fallback={<Loader />}>
              {videoEmbed && (
                <div
                  data-type="video-iframe-wrapper"
                  className="w-full h-full"
                  data-rte="true"
                  dangerouslySetInnerHTML={createMarkup(videoEmbed.html)}
                />
              )}
            </Suspense>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

const CardListIcon = ({ cardItem, i }) => {
  return (
    <div
      key={i}
      className={`relative w-full py-0 md:pt-4 px-0 md:pb-8 overflow-hidden md:[&:nth-child(3n+3)]:col-start-5 
    md:[&:nth-child(3n+2)]:col-start-3 
    md:[&:nth-child(3n+1)]:col-start-1 
    md:[&:last-child:first-child]:col-start-3 
    md:[&:last-child:nth-child(3n+1)]:col-start-3 
    md:[&:nth-last-child(2):first-child]:col-start-2 
    md:[&:nth-last-child(2):nth-child(3n+1)]:col-start-2 
    md:[&:nth-child(2):last-child]:col-start-4 
    md:[&:last-child:nth-child(3n+2)]:col-start-4 col-span-2`}
    >
      <div className={`relative grid grid-cols-8 md:grid-cols-4 lg:grid-cols-6 items-start gap-3`}>
        <div className="w-12 col-start-1 col-span-1">
          <Picture
            desktopImage={cardItem.desktopImage}
            mobileImage={cardItem.mobileImage}
            className={`mx-auto mb-5 aspect-square`}
          />
        </div>
        <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5 px-4">
          {cardItem.title && <HeroTitle type="curated" cardItem={cardItem} idx={i} />}
          {cardItem.description && (
            <div className="mt-2" data-rte="true" dangerouslySetInnerHTML={createMarkup(cardItem.description)} />
          )}
        </div>
      </div>
    </div>
  )
}

const CardListHeroImage = ({ cardItem, i }) => {
  return (
    <div
      key={i}
      className={`relative w-full px-0 rounded-2xl overflow-hidden md:[&:nth-child(3n+3)]:col-start-5 
    md:[&:nth-child(3n+2)]:col-start-3 
    md:[&:nth-child(3n+1)]:col-start-1 
    md:[&:last-child:first-child]:col-start-3 
    md:[&:last-child:nth-child(3n+1)]:col-start-3 
    md:[&:nth-last-child(2):first-child]:col-start-2 
    md:[&:nth-last-child(2):nth-child(3n+1)]:col-start-2 
    md:[&:nth-child(2):last-child]:col-start-4 
    md:[&:last-child:nth-child(3n+2)]:col-start-4 col-span-2 `}
    >
      <div className={`relative flex flex-col`}>
        <Picture
          desktopImage={cardItem.desktopImage}
          mobileImage={cardItem.mobileImage}
          className={`mx-auto mb-5 aspect-video rounded-2xl`}
        />
        <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5 px-4">
          {/* {cardItem.dateTimeFormatted} */}
          {cardItem.title && <HeroTitle type="curated" cardItem={cardItem} idx={i} />}
          {cardItem.description && (
            <div className="mt-2" data-rte="true" dangerouslySetInnerHTML={createMarkup(cardItem.description)} />
          )}
        </div>
      </div>
    </div>
  )
}

function CardListCurated(props) {
  let cardItems = props.cardItems || []
  const initialNumber = props.initialNoOfCards || 3
  const [viewMore, setViewMore] = useState(cardItems.length <= initialNumber)
  const [variant, setVariant] = useState('background')
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  if (!viewMore) {
    cardItems = cardItems.slice(0, initialNumber)
  }

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'around'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      return newState
    })
    setVariant((prevState) => {
      // Background Image => background
      // Hero Image => hero
      // Icon => icon
      if (!props.cardVariant) return 'background'

      return props.cardVariant?.split(' ')[0].toLowerCase()
    })
  }, [])

  return (
    <Section
      type="cardListCurated"
      innerClass={`px-7 text-${textAlignment.align} ${componentPadding}`}
      headingClass="w-full"
      {...props}
    >
      <>
        {cardItems.length != 0 && (
          <div
            className={`grid grid-cols-1 md:grid-cols-6 ${props.title?.length || props.description?.length ? 'pt-10' : 'pt-0'}
          items-start gap-8`}
          >
            {/* ${Math.min(cardItems.length, Math.max(cardItems.length, 3))} */}
            {cardItems.map((cardItem, i) => {
              if (variant === 'background') {
                return <CardListBGImage {...props} cardItem={cardItem} i={i} textAlignment={textAlignment} />
              } else if (variant === 'hero') {
                return <CardListHeroImage {...props} cardItem={cardItem} i={i} />
              }
              return <CardListIcon {...props} cardItem={cardItem} i={i} />
            })}
          </div>
        )}
        {!viewMore && (
          <div className="flex justify-center mt-20">
            <ButtonEl
              onClick={() => {
                setViewMore(true)
              }}
            >
              {props.viewMoreText || 'View more'}
              <ArrowRightIcon role="none" className="h-6 w-6 ml-2" />
            </ButtonEl>
          </div>
        )}
      </>
    </Section>
  )
}

export default CardListCurated
